import { useEffect, useMemo } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { BehaviorSubject, distinctUntilChanged, map } from "rxjs";
import { OmitByValue, PickByValue } from "utility-types";

type T = { [key in string]: string | number | boolean | string[] | number[] | Object | null };

type ParamValue = string | number | boolean | string[] | number[] | Object | null;

const paramsToObject = <T>(p: URLSearchParams) => {
  const result: { [key in string]: string } = {} as never;
  for (const [key, value] of p.entries()) {
    result[key] = value as never;
  }
  return result as T;
};

const getSearchParams = <T>(p?: URLSearchParams) =>
  paramsToObject<T>(p || new URLSearchParams(window.location.search));

const getHashParams = <T>(hash?: string) =>
  paramsToObject<T>(new URLSearchParams((hash ?? window.location.hash).replace(/^#/, "")));

const applyUpdates = (params: URLSearchParams, val: Record<string, ParamValue | undefined>) => {
  Object.keys(val).forEach((key) => {
    const value = val[key];
    if (
      value === null ||
      value === undefined ||
      (Array.isArray(value) && (!value.length || value.find((a) => a !== null) === undefined)) ||
      (typeof value === "object" && !Object.keys(value)?.length)
    ) {
      params.delete(key);
    } else if (typeof value === "object" || Array.isArray(value)) {
      params.set(key, JSON.stringify(value));
    } else {
      params.set(key, value.toString());
    }
  });
};

export class SearchQueryManager<TT extends T, HH extends T = T> {
  private _search$ = new BehaviorSubject<TT>(this.initValue ?? getSearchParams<TT>());
  private _hash$ = new BehaviorSubject<HH>(this.initHash ?? getHashParams<HH>());
  public search$ = this._search$.asObservable().pipe(distinctUntilChanged());
  public hash$ = this._hash$.asObservable().pipe(distinctUntilChanged());

  constructor(
    private readonly navigate: NavigateFunction,
    private readonly initValue?: TT, // for testing
    private readonly initHash?: HH, // for testing
  ) {}

  public setSearchQuery = (val: { [key in keyof TT]?: ParamValue }) => {
    const search = new URLSearchParams(window.location.search);
    applyUpdates(search, val as Record<string, ParamValue | undefined>);
    this.navigate("?" + search.toString(), {
      replace: true,
      relative: "route",
    });
    this._search$.next(getSearchParams(search));
  };

  public setHashQuery = (val: { [key in keyof HH]?: ParamValue }) => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    applyUpdates(hashParams, val as Record<string, ParamValue | undefined>);
    const next = hashParams.toString();
    this.navigate(
      {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: next ? `#${next}` : "",
      },
      { replace: true },
    );
    this._hash$.next(getHashParams(next));
  };

  public updateSearch = (search: string) => this._search$.next(getSearchParams(new URLSearchParams(search)));
  public updateHash = (hash: string) => this._hash$.next(getHashParams<HH>(hash));

  public getString$ = (key: keyof PickByValue<TT, string>, defaultStr?: string) =>
    this.search$.pipe(map((s) => s[key] || defaultStr)).pipe(distinctUntilChanged());
  public getNumber$ = (key: keyof PickByValue<TT, number>, defaultNum?: number) =>
    this.search$.pipe(map((s) => (s[key] ? Number(s[key]) : defaultNum))).pipe(distinctUntilChanged());
  public getBoolean$ = (key: keyof PickByValue<TT, boolean>, defaultBool?: boolean) =>
    this.search$
      .pipe(map((s) => (s[key] ? (s[key] === "true" ? true : false) : defaultBool)))
      .pipe(distinctUntilChanged());
  public getCurrentValue = <TKey extends keyof TT>(key: TKey, defaultVal?: TT[TKey]): string | undefined =>
    (this._search$.value[key] as never) || defaultVal;
  public getCurrentValueObject = <TKey extends keyof TT>(key: TKey, defaultVal?: TT[TKey]) =>
    (this._search$.value
      ? JSON.parse((this._search$.value[key] as never) ?? "null") || defaultVal
      : defaultVal) as TT[TKey];
  public getObject$ = <K extends keyof OmitByValue<TT, string | number | boolean | Date>, X extends TT[K]>(
    key: K,
    defaultObj?: X,
  ) =>
    this.search$.pipe(
      map((s) => (s[key] ? JSON.parse(s[key] as never) : defaultObj) as TT[K]),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );

  public getHashString$ = (key: keyof PickByValue<HH, string>, defaultStr?: string) =>
    this.hash$.pipe(map((s) => s[key] || defaultStr)).pipe(distinctUntilChanged());
  public getHashNumber$ = (key: keyof PickByValue<HH, number>, defaultNum?: number) =>
    this.hash$.pipe(map((s) => (s[key] ? Number(s[key]) : defaultNum))).pipe(distinctUntilChanged());
  public getHashBoolean$ = (key: keyof PickByValue<HH, boolean>, defaultBool?: boolean) =>
    this.hash$.pipe(map((s) => (s[key] ? s[key] === "true" : defaultBool))).pipe(distinctUntilChanged());
  public getCurrentHashValue = <TKey extends keyof HH>(
    key: TKey,
    defaultVal?: HH[TKey],
  ): string | undefined => (this._hash$.value[key] as never) || defaultVal;
}

export const useQueryManager = <TT extends T, HH extends T = T>() => {
  const navigate = useNavigate();
  const location = useLocation();
  const bloc = useMemo(() => new SearchQueryManager<TT, HH>(navigate), [navigate]);
  useEffect(() => {
    bloc.updateSearch(location.search);
  }, [location.search]);
  useEffect(() => {
    bloc.updateHash(location.hash);
  }, [location.hash]);
  return bloc;
};
